import { useCallback, useEffect, useRef, useState } from "react";
import type { ScreenBox, UseAreaSelectGesture } from "./types";

const CLICK_TOLERANCE_PX = 5;

const isTyping = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName);

export const useAreaSelectGesture: UseAreaSelectGesture = ({
  containerRef,
  panOffset,
  zoomLevel,
  onAreaSelect,
}) => {
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const isSpaceDownRef = useRef(false);
  const [selectionBox, setSelectionBox] = useState<ScreenBox | null>(null);
  const [isSpaceDown, setIsSpaceDown] = useState(false);

  const localPoint = useCallback(
    (event: { clientX: number; clientY: number }) => {
      const box = containerRef.current?.getBoundingClientRect();
      return box ? { x: event.clientX - box.left, y: event.clientY - box.top } : null;
    },
    [containerRef],
  );

  const cancel = useCallback(() => {
    startRef.current = null;
    setSelectionBox(null);
  }, []);

  const handleMouseDown: ReturnType<UseAreaSelectGesture>["handleMouseDown"] = (event) => {
    // In the mode a drag draws the box, as in any selection tool; Space-drag or the middle button pans.
    if (!onAreaSelect || event.button !== 0 || isSpaceDownRef.current) return false;
    const start = localPoint(event);
    if (!start) return false;
    startRef.current = start;
    setSelectionBox({ from: start, to: start });
    event.preventDefault();
    event.stopPropagation();
    return true;
  };

  const handleMouseMove: ReturnType<UseAreaSelectGesture>["handleMouseMove"] = (event) => {
    const start = startRef.current;
    if (!start) return false;
    const to = localPoint(event);
    if (to) setSelectionBox({ from: start, to });
    return true;
  };

  const handleMouseUp: ReturnType<UseAreaSelectGesture>["handleMouseUp"] = (event, onClick) => {
    const start = startRef.current;
    if (!start) return false;
    cancel();
    const end = localPoint(event) ?? start;
    event.preventDefault();
    event.stopPropagation();
    const isClick =
      Math.abs(end.x - start.x) < CLICK_TOLERANCE_PX &&
      Math.abs(end.y - start.y) < CLICK_TOLERANCE_PX;
    if (isClick) {
      onClick();
      return true;
    }
    const toPlan = (point: { x: number; y: number }) => ({
      x: (point.x - panOffset.x) / zoomLevel,
      y: (point.y - panOffset.y) / zoomLevel,
    });
    const a = toPlan(start);
    const b = toPlan(end);
    onAreaSelect?.({
      x1: Math.min(a.x, b.x),
      y1: Math.min(a.y, b.y),
      x2: Math.max(a.x, b.x),
      y2: Math.max(a.y, b.y),
      isAdditive: Boolean(event.shiftKey),
    });
    return true;
  };

  // While selecting by area, holding Space turns the drag back into panning.
  useEffect(() => {
    if (!onAreaSelect) return;
    const down = (event: KeyboardEvent) => {
      if (event.code !== "Space" || isTyping(event.target)) return;
      isSpaceDownRef.current = true;
      setIsSpaceDown(true);
    };
    const up = (event: KeyboardEvent) => {
      if (event.code !== "Space") return;
      isSpaceDownRef.current = false;
      setIsSpaceDown(false);
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      isSpaceDownRef.current = false;
      setIsSpaceDown(false);
    };
  }, [onAreaSelect]);

  return { selectionBox, isSpaceDown, handleMouseDown, handleMouseMove, handleMouseUp, cancel };
};
